class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: { [key: string]: ((data: any) => void)[] } = {};
  private typingThrottleTimeout: NodeJS.Timeout | null = null;

  connect(groupId: string, taskId: string, token: string) {
    // Use Django backend URL for WebSocket connection
    const backendUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${groupId}/${taskId}/`;
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    this.ws = new WebSocket(wsUrl);
    
    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      
      // Send authentication token
      this.send({
        type: 'auth',
        token: token
      });
    };
    
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    this.ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.handleReconnect(groupId, taskId, token);
    };
    
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private handleReconnect(groupId: string, taskId: string, token: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      
      setTimeout(() => {
        this.connect(groupId, taskId, token);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
      this.emit('connection_failed', { message: 'Unable to connect to chat server' });
    }
  }

  private handleMessage(data: any) {
    const { type, ...messageData } = data;
    
    if (this.listeners[type]) {
      this.listeners[type].forEach(callback => callback(messageData));
    }
    
    // Emit generic message event
    if (this.listeners['message']) {
      this.listeners['message'].forEach(callback => callback(data));
    }
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  sendMessage(content: string, messageType: string = 'text', replyTo?: string) {
    this.send({
      type: 'chat_message',
      content,
      message_type: messageType,
      reply_to: replyTo
    });
  }

  sendTypingStart() {
    // Throttle typing start events to prevent spam
    if (this.typingThrottleTimeout) {
      return; // Skip if already throttled
    }
    
    this.send({
      type: 'typing_start',
      user_name: this.getCurrentUserName()
    });
    
    // Throttle for 100ms to prevent excessive events (faster)
    this.typingThrottleTimeout = setTimeout(() => {
      this.typingThrottleTimeout = null;
    }, 100);
  }

  sendTypingStop() {
    this.send({
      type: 'typing_stop',
      user_name: this.getCurrentUserName()
    });
  }

  sendMessageRead(messageId: string) {
    this.send({
      type: 'message_read',
      message_id: messageId
    });
  }

  on(event: string, callback: (data: any) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback: (data: any) => void) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  private emit(event: string, data: any) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  private getCurrentUserName(): string {
    // Get user name from localStorage or auth context
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.full_name || user.name || user.username || 'User';
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    return 'User';
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.listeners = {};
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export default new WebSocketService();
